import type {CurrentProject} from 'common/report/model-resources/projects';
import {ProjectDetails} from 'common/report/stores/project-details';
import {getProjectId, isCurrentProjectInternal, isUrlDefinedExperimentalFeatureEnabled} from 'common/report/util';
import {isDevEnv, isTestEnv} from 'common/report/util/env';
import {getLocation} from 'common/util/url';
import {FEATURE_FLAG_VARIANTS} from 'apps/feature-flags/src/constants';

import {FeatureFlag, FeatureGate, ProjectFeatureFlag} from '../constants';

export const featureSamplingUtils = {
  determineFeatureEligibility,
};

const CONTROL_VARIANT = `control`;

/**
 * Check deterministically whether a user or group is included in a feature rollout/test based on a
 * unique user/group string identifier and the desired percentage to include.
 */
function determineFeatureEligibility(
  userOrGroupId: string,
  feature: string,
  percentAllowed: number /* must be in range [0, 100] */,
): boolean {
  if (!(percentAllowed >= 0 && percentAllowed <= 100)) {
    throw new Error(`@percentAllowed argument must be in range [0, 100] inclusive`);
  }

  if (typeof userOrGroupId === `undefined` || userOrGroupId === null || userOrGroupId === ``) {
    throw new Error(`@userOrGroupId argument cannot be null or undefined or empty`);
  }
  if (typeof feature === `undefined` || feature === null || feature === ``) {
    throw new Error(`@feature argument cannot be null or undefined or empty`);
  }

  switch (percentAllowed) {
    case 0:
      return false;
    case 100:
      return true;
    default:
      break;
  }

  const key = userOrGroupId + feature;

  // Bernstein's hash: http://www.cse.yorku.ca/~oz/hash.html#djb2
  // The djb2 hash was chosen because of the claim that "it has excellent distribution and speed on many different sets of keys and table sizes",
  // but we have not validated whether it is statistically reliable and accurate to use in sampling users/groups for rollouts and analysis.
  let hash = 5381;
  for (let i = 0; i < key.length; i++) {
    hash = (hash << 5) + hash + key.charCodeAt(i);
    hash = hash & hash;
  }
  const dart = (hash >>> 0) % 100;
  return dart < percentAllowed;
}

enum FeatureRolloutType {
  Project,
  Org,
}
export class FeatureRollout {
  private declare type: FeatureRolloutType;
  private declare percentage: number;
  public declare readonly featureflag: ProjectFeatureFlag | undefined;
  private explicitlyDisallowIds: string[] = [];

  public declare readonly name: FeatureRolloutName;

  constructor(params: {
    type: FeatureRolloutType;
    name: FeatureRolloutName;
    percentage: number;
    featureflag?: ProjectFeatureFlag;
    explicitlyDisallowIds?: string[];
  }) {
    this.type = params.type;
    this.name = params.name;
    this.percentage = params.percentage;
    this.featureflag = params.featureflag;
    this.explicitlyDisallowIds = params.explicitlyDisallowIds ?? [];
  }

  isEnabled(project: CurrentProject | ProjectDetails | null): boolean {
    if (this.type === FeatureRolloutType.Project) {
      const projectId = project?.id.toString() ?? getProjectId();
      if (this.explicitlyDisallowIds.includes(projectId)) {
        return false;
      }

      return projectId
        ? featureSamplingUtils.determineFeatureEligibility(projectId, this.name.toString(), this.percentage) ||
            this.hasProjectFeatureFlag(project)
        : false;
    }
    const orgId = project?.organizationId.toString();
    if (orgId !== undefined && this.explicitlyDisallowIds.includes(orgId)) {
      return false;
    }

    return orgId
      ? featureSamplingUtils.determineFeatureEligibility(orgId, this.name.toString(), this.percentage) ||
          this.hasProjectFeatureFlag(project)
      : false;
  }

  private hasProjectFeatureFlag(project: CurrentProject | ProjectDetails | null): boolean {
    return this.featureflag !== undefined && (project?.hasFeatureFlag(this.featureflag) ?? false);
  }
}

// Add any gradual feature rollouts here
export enum FeatureRolloutName {
  OnboardingChecklist = `onboarding-checklist`,
  Revenue = `revenue`,
  Home = `home`,
  WarehouseLaunchTemplates = `wh_launch_templates`,
  DisableVizOnNonCompliant = `disable_viz_non_comp`,
  CompanyProfile = `company_profile`,
  BoardBreakdowns = `board_breakdowns`,
  NullPropertyMonitoring = `null_property_monitoring`,
  OptimizedFunnelSelection = `optimized_funnels`,
  PropertyTypeMonitoring = `property_type_monitoring`,
  ConcurrentReplays = `sr_concurrent_replays`,
  TextAnalysis = `text_analysis`,
  IssuesHub = `issues_hub`,
  IssuesHubSidePanel = `issues_hub_sidepanel`,
  EnableVerifiedCohorts = `enable_verified_cohorts`,
  WebhookAlerts = `webhook_alerts`,
  CustomAlertsCharts = `custom_alerts_charts`,
  ExperimentSegmentation = `experiment_segmentation`,
  Passkeys = `passkeys`,
  SelfServiceTwoFactor = `self_service_twofactor`,
}

export const FeatureRollouts: {[key in FeatureRolloutName]: FeatureRollout} = {
  [FeatureRolloutName.OnboardingChecklist]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.OnboardingChecklist,
    percentage: 0,
  }),
  [FeatureRolloutName.Revenue]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.Revenue,
    featureflag: ProjectFeatureFlag.Revenue,
    percentage: 0,
  }),
  [FeatureRolloutName.Home]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.Home,
    percentage: 100,
    explicitlyDisallowIds: [`1866253`],
  }),
  [FeatureRolloutName.WarehouseLaunchTemplates]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.WarehouseLaunchTemplates,
    percentage: 0,
    featureflag: ProjectFeatureFlag.WarehouseLaunchTemplates,
  }),
  [FeatureRolloutName.DisableVizOnNonCompliant]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.DisableVizOnNonCompliant,
    percentage: 0,
    featureflag: ProjectFeatureFlag.DisableVizOnNonCompliant,
  }),
  [FeatureRolloutName.CompanyProfile]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.CompanyProfile,
    percentage: 100,
    featureflag: ProjectFeatureFlag.CompanyProfile,
  }),
  [FeatureRolloutName.ConcurrentReplays]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.ConcurrentReplays,
    percentage: 0,
    featureflag: ProjectFeatureFlag.ConcurrentReplays,
  }),
  [FeatureRolloutName.BoardBreakdowns]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.BoardBreakdowns,
    percentage: 100,
    featureflag: ProjectFeatureFlag.BoardBreakdowns,
  }),
  [FeatureRolloutName.NullPropertyMonitoring]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.NullPropertyMonitoring,
    percentage: 50,
    featureflag: ProjectFeatureFlag.NullPropertyMonitoring,
  }),
  [FeatureRolloutName.PropertyTypeMonitoring]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.PropertyTypeMonitoring,
    percentage: 100,
    featureflag: ProjectFeatureFlag.PropertyTypeMonitoring,
  }),
  [FeatureRolloutName.OptimizedFunnelSelection]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.OptimizedFunnelSelection,
    percentage: 0,
    featureflag: ProjectFeatureFlag.OptimizedFunnelSelection,
  }),
  [FeatureRolloutName.TextAnalysis]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.TextAnalysis,
    percentage: 0,
    featureflag: ProjectFeatureFlag.TextAnalysis,
  }),
  [FeatureRolloutName.IssuesHub]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.IssuesHub,
    percentage: 0,
    featureflag: ProjectFeatureFlag.LexiconIssuesHub,
  }),
  [FeatureRolloutName.IssuesHubSidePanel]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.IssuesHubSidePanel,
    percentage: 100,
    featureflag: ProjectFeatureFlag.LexiconIssuesHubSidePanel,
  }),
  [FeatureRolloutName.EnableVerifiedCohorts]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.EnableVerifiedCohorts,
    percentage: 100,
    featureflag: ProjectFeatureFlag.EnableVerifiedCohorts,
  }),
  [FeatureRolloutName.WebhookAlerts]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.WebhookAlerts,
    percentage: 100,
    featureflag: ProjectFeatureFlag.WebhookAlerts,
  }),
  [FeatureRolloutName.CustomAlertsCharts]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.CustomAlertsCharts,
    percentage: 100,
    featureflag: ProjectFeatureFlag.CustomAlertsCharts,
  }),
  [FeatureRolloutName.ExperimentSegmentation]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.ExperimentSegmentation,
    percentage: 0,
    featureflag: ProjectFeatureFlag.ExperimentSegmentation,
  }),
  [FeatureRolloutName.Passkeys]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.Passkeys,
    percentage: 100,
    featureflag: ProjectFeatureFlag.EnablePasskeyCreation,
  }),
  [FeatureRolloutName.SelfServiceTwoFactor]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.SelfServiceTwoFactor,
    percentage: 100,
    featureflag: ProjectFeatureFlag.SelfServiceTwoFactor,
  }),
};
export function useTemplateSearch() {
  if (isTestEnv()) {
    return true;
  }
  return isUrlDefinedExperimentalFeatureEnabled(getLocation(), `template_search`, () => isCurrentProjectInternal());
}

export function isChunkedReplayPlayerEnabled() {
  const variant = getFeatureFlagVariant(FeatureFlag.SessionReplayPlayerRefactor);
  return !!variant && variant === FEATURE_FLAG_VARIANTS.Treatment;
}

export function isDataVolumeMonitoringEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }
  return currentProject.getFeatureGate(FeatureGate.DataGovernance);
}

export function getSessionRecordingBlockingInfo(currentProject?: CurrentProject) {
  if (!currentProject) {
    return [];
  }
  return currentProject.blocking.blockedReplayWindows || [];
}

export function isSessionRecordingEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }

  const hasSessionReplayFeatureGate = currentProject.getFeatureGate(FeatureGate.SessionReplay);
  const hasFeatureFlagEnabled = currentProject.hasFeatureFlag(ProjectFeatureFlag.SessionRecording);
  const hasSessionReplayEnabled = hasSessionReplayFeatureGate || hasFeatureFlagEnabled;
  if (currentProject.hasFeatureFlag(ProjectFeatureFlag.SessionRecordingSensitiveData)) {
    return hasSessionReplayEnabled && currentProject.hasSensitiveAccess;
  }

  return hasSessionReplayEnabled;
}

export const INTEGRATIONS_V2_VARIANTS = {
  CONTROL: `control`, // Group A: V1 UI in settings
  V2_SETTINGS: `v2_settings`, // Group B: V2 UI in settings
  V2_DATA_MENU: `v2_data_menu`, // Group C: V2 UI in data menu
} as const;

export function getIntegrationsV2Variant(): string {
  const variant = getFeatureFlagVariant(FeatureFlag.IntegrationsV2UI, INTEGRATIONS_V2_VARIANTS.CONTROL);
  return variant || INTEGRATIONS_V2_VARIANTS.CONTROL;
}

export function shouldShowIntegrationsV2(): boolean {
  const variant = getIntegrationsV2Variant();
  return variant !== INTEGRATIONS_V2_VARIANTS.CONTROL;
}

export function shouldShowDestinationsInDataMenu(): boolean {
  const variant = getIntegrationsV2Variant();
  return variant === INTEGRATIONS_V2_VARIANTS.V2_DATA_MENU;
}

export function shouldUseQuerySharedWorkerSemaphore(): boolean {
  return (
    (getFeatureFlagVariant(FeatureFlag.QuerySharedWorkerSemaphore, CONTROL_VARIANT) ?? CONTROL_VARIANT) !==
    CONTROL_VARIANT
  );
}

export function shouldUseQuerySoftThrottle(): boolean {
  return (getFeatureFlagVariant(FeatureFlag.QuerySoftThrottle, CONTROL_VARIANT) ?? CONTROL_VARIANT) !== CONTROL_VARIANT;
}

export function isSegmentRegionEnabled() {
  return true;
}

export function isDataStandardsEnabled(currentProject?: CurrentProject | ProjectDetails) {
  return isDevEnv() || currentProject?.getFeatureGate(FeatureGate.DataStandards);
}

export function isAutomateEventApprovalEnabled(currentProject?: CurrentProject | ProjectDetails) {
  return currentProject?.getFeatureGate(FeatureGate.DataStandards);
}

export function isDisableVisibleOnNonCompliantEnabled(currentProject?: CurrentProject | ProjectDetails): boolean {
  if (isDevEnv()) {
    return true;
  }

  if (!currentProject) {
    return false;
  }

  return Boolean(
    currentProject.getFeatureGate(FeatureGate.DataStandards) && // has data standards feature gate
      isDataStandardsEnabled(currentProject) && // data standards feature is enabled
      currentProject.getFeatureGate(FeatureGate.DataGovernance) && // has event approval feature gate, this check is necessary in case account downgrade
      currentProject?.hasEventApprovalEnabled && // event approval is enabled and turned on
      FeatureRollouts[FeatureRolloutName.DisableVizOnNonCompliant].isEnabled(currentProject),
  );
}

export function isCompanyProfileEnabled(currentProject?: CurrentProject | ProjectDetails | null) {
  if (isDevEnv()) {
    return true;
  }
  return FeatureRollouts[FeatureRolloutName.CompanyProfile].isEnabled(currentProject ?? null);
}

export function isMetricLevelDataGroupsEnabled(currentProject?: CurrentProject | ProjectDetails | null) {
  if (!currentProject) {
    return false;
  }
  return (
    currentProject.hasFeatureFlag(ProjectFeatureFlag.FirstClassMetrics) ||
    // Also check metric level data groups feature flag if its in a testing environment
    // Till we setup ReportBookmarkManagerProvider for b2b-metrics.stories.tsx
    (currentProject.hasFeatureFlag(ProjectFeatureFlag.MetricLevelDataGroups) && isTestEnv())
  );
}

export function isCanvasEnabled(currentProject: CurrentProject | null) {
  if (!currentProject) {
    return false;
  }
  return currentProject.getFeatureGate(FeatureGate.MetricTree);
}

export function isGoalEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }

  return currentProject.hasFeatureFlag(ProjectFeatureFlag.Goal);
}

export function isNullPropertyMonitoringEnabled(currentProject: CurrentProject | null | undefined) {
  return (
    FeatureRollouts[FeatureRolloutName.NullPropertyMonitoring].isEnabled(currentProject ?? null) ||
    isDevEnv() ||
    isCurrentProjectInternal()
  );
}

function getFeatureFlagVariant(flagName: FeatureFlag, fallbackValue?: string) {
  if (typeof mixpanel === `undefined`) {
    return fallbackValue;
  }

  return mixpanel.flags?.get_variant_value_sync(flagName, fallbackValue);
}

/**
 * Get a feature flag's variant value asynchronously.
 * Async counterpart to getFeatureFlagVariant: awaits flags being loaded before resolving, so the
 * result reflects the actual variant instead of the fallback that the sync check returns before
 * flags are ready.
 */
export async function getFeatureFlagVariantAsync(
  flagName: FeatureFlag,
  fallbackValue?: string,
): Promise<string | undefined> {
  if (typeof mixpanel === `undefined`) {
    return fallbackValue;
  }
  return (await mixpanel.flags?.get_variant_value(flagName, fallbackValue)) ?? fallbackValue;
}

/**
 * Merge new values into the feature-flag evaluation context and await the SDK's flag re-fetch,
 * so a subsequent variant read evaluates against the updated context.
 */
export async function updateFeatureFlagContext(context: Record<string, unknown>): Promise<void> {
  if (typeof mixpanel === `undefined`) {
    return;
  }
  await mixpanel.flags?.update_context(context);
}

/**
 * Check if a feature flag is enabled synchronously.
 * Similar to isFeatureFlagEnabled but uses synchronous check without currentProject.
 */
export function isFeatureFlagEnabledSync(flagName: FeatureFlag, fallbackValue: boolean = false): boolean {
  if (typeof mixpanel === `undefined`) {
    return fallbackValue;
  }
  return mixpanel.flags?.is_enabled_sync(flagName, fallbackValue) ?? fallbackValue;
}

/**
 * Check if a feature flag is enabled asynchronously.
 * Async counterpart to isFeatureFlagEnabledSync: awaits flags being loaded before
 * resolving, so the result reflects the actual flag value instead of the fallback that
 * the sync check returns before flags are ready. Respects flag persistence.
 */
export async function isFeatureFlagEnabledAsync(
  flagName: FeatureFlag,
  fallbackValue: boolean = false,
): Promise<boolean> {
  if (typeof mixpanel === `undefined`) {
    return fallbackValue;
  }
  return (await mixpanel.flags?.is_enabled(flagName, fallbackValue)) ?? fallbackValue;
}

/**
 * Check if an feature gate feature flag is enabled via isFeatureFlagEnabledSync
 * AND the org has Mixpanel AI turned on (the org-level "Enable Mixpanel AI" toggle / spark_ai_enabled).
 * Use this for any AI feature so it stays gated on the org setting.
 *
 * The sparkAIEnabled check is inlined (rather than calling isAIEnabled from
 * dashboards/utils) to keep this module's import graph free of DOM-dependent
 * modules — many node-environment unit tests transitively import this file.
 */
export function isAIFeatureFlagEnabledSync(
  flagName: FeatureFlag,
  currentProject: CurrentProject | null | undefined,
  fallbackValue: boolean = false,
): boolean {
  if (!currentProject?.sparkAIEnabled) {
    return false;
  }
  return isFeatureFlagEnabledSync(flagName, fallbackValue);
}

// Use when using a feature gate feature flag and checking for enablement.
export function isFeatureFlagEnabled(
  currentProject: CurrentProject | null | undefined,
  flagName: FeatureFlag,
  projectFeatureFlagName: ProjectFeatureFlag,
) {
  return currentProject?.hasFeatureFlag(projectFeatureFlagName) || mixpanel.flags?.is_enabled(flagName, false);
}

export function isIssuesHubEnabled(currentProject?: CurrentProject | ProjectDetails) {
  return (
    FeatureRollouts[FeatureRolloutName.IssuesHub].isEnabled(currentProject ?? null) ||
    isDevEnv() ||
    isCurrentProjectInternal()
  );
}

export function isIssuesHubSidePanelEnabled(currentProject?: CurrentProject | ProjectDetails) {
  return (
    FeatureRollouts[FeatureRolloutName.IssuesHubSidePanel].isEnabled(currentProject ?? null) ||
    currentProject?.hasFeatureFlag(ProjectFeatureFlag.LexiconIssuesHubSidePanel)
  );
}

export function isPropertyTypeMonitoringEnabled(currentProject: CurrentProject | null | undefined) {
  return (
    FeatureRollouts[FeatureRolloutName.PropertyTypeMonitoring].isEnabled(currentProject ?? null) ||
    currentProject?.hasFeatureFlag(ProjectFeatureFlag.PropertyTypeMonitoring)
  );
}

export function isVerifiedCohortsEnabled(currentProject?: CurrentProject | ProjectDetails | null): boolean {
  return (
    FeatureRollouts[FeatureRolloutName.EnableVerifiedCohorts].isEnabled(currentProject ?? null) ||
    Boolean(currentProject?.hasFeatureFlag(ProjectFeatureFlag.EnableVerifiedCohorts))
  );
}

export function isAdvancedDeletionEventCountBypassEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }

  return currentProject.hasFeatureFlag(ProjectFeatureFlag.AdvDelEventCountBypass);
}

export function isAdvancedDeletionRequestPerMonthBypassEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }

  return currentProject.hasFeatureFlag(ProjectFeatureFlag.AdvDelRequestPerMonthBypass);
}

export function isJHSAdvancedDropEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }

  return currentProject?.hasFeatureFlag(ProjectFeatureFlag.JHSAdvancedDrop);
}

export function isAllEventsDisabled(currentProject: CurrentProject | undefined): boolean {
  return Boolean(currentProject?.hasFeatureFlag(ProjectFeatureFlag.DisableAllEvents));
}

export function isBoardsMadeEasyEnabled(currentProject: CurrentProject | undefined): boolean {
  return (
    currentProject?.hasFeatureFlag(ProjectFeatureFlag.BoardsMadeEasy) ||
    mixpanel.flags?.get_variant_value_sync(FeatureFlag.BoardsMadeEasy, false)
  );
}

export function isRetentionAwarenessEnabled(currentProject: CurrentProject | undefined): boolean {
  if (currentProject?.hasFeatureFlag(ProjectFeatureFlag.RetentionAwareness)) {
    return true;
  }
  const RETENTION_AWARENESS_CACHE_KEY = `$mp_retention_awareness_flag`;

  // Caching this feature flag since it gets evaluated on every project metadata load
  // and we want to avoid sending redundant $experiment_started events
  try {
    const cached = window.sessionStorage.getItem(RETENTION_AWARENESS_CACHE_KEY);
    if (cached) {
      return cached === FEATURE_FLAG_VARIANTS.Enabled;
    }
  } catch {
    // fall through to SDK call
  }

  const variant = getFeatureFlagVariant(FeatureFlag.RetentionAwareness);
  try {
    if (variant) {
      window.sessionStorage.setItem(RETENTION_AWARENESS_CACHE_KEY, variant);
    }
  } catch {
    // continue without caching
  }
  return !!variant && variant === FEATURE_FLAG_VARIANTS.Enabled;
}

export function isChartTrendlinesEnabled(currentProject: CurrentProject | undefined): boolean {
  return currentProject?.hasFeatureFlag(ProjectFeatureFlag.ChartTrendlines) || isCurrentProjectInternal();
}

export function isWebhookAlertsEnabled(currentProject?: CurrentProject | ProjectDetails | null): boolean {
  return FeatureRollouts[FeatureRolloutName.WebhookAlerts].isEnabled(currentProject ?? null);
}

export function isVersionHistoryEnabled(currentProject?: CurrentProject): boolean {
  return (
    currentProject?.hasFeatureFlag(ProjectFeatureFlag.VersionHistory) ||
    mixpanel.flags?.get_variant_value_sync(FeatureFlag.VersionHistory, false)
  );
}

export function isContentDiagnosticsTabEnabled(currentProject?: CurrentProject | null): boolean {
  return (
    currentProject?.hasFeatureFlag(ProjectFeatureFlag.ContentDiagnosticsTab) ||
    isFeatureFlagEnabledSync(FeatureFlag.ContentDiagnosticsTab)
  );
}

export function isPasskeysEnabled(currentProject?: CurrentProject | null): boolean {
  return FeatureRollouts[FeatureRolloutName.Passkeys].isEnabled(currentProject ?? null);
}

export function isSelfServiceTwoFactorEnabled(currentProject?: CurrentProject | null): boolean {
  return FeatureRollouts[FeatureRolloutName.SelfServiceTwoFactor].isEnabled(currentProject ?? null);
}

export function isSparkReportAutoNameEnabled(currentProject?: CurrentProject): boolean {
  const legacyCheck = currentProject?.hasFeatureFlag(ProjectFeatureFlag.SparkReportAutoName);
  if (legacyCheck) {
    return true;
  }

  const variant = getFeatureFlagVariant(FeatureFlag.SparkReportAutoName);
  return !!variant && variant === FEATURE_FLAG_VARIANTS.Treatment;
}

export function isSparkCohortAutoNameEnabled(currentProject?: CurrentProject): boolean {
  const legacyCheck = currentProject?.hasFeatureFlag(ProjectFeatureFlag.SparkCohortAutoName);
  if (legacyCheck) {
    return true;
  }

  const variant = getFeatureFlagVariant(FeatureFlag.SparkCohortAutoName);
  return !!variant && variant === FEATURE_FLAG_VARIANTS.Treatment;
}

export function isSparkCopilotEnabled(currentProject?: CurrentProject): boolean {
  return currentProject?.hasFeatureFlag(ProjectFeatureFlag.SparkCopilot) ?? false;
}

// Commented to avoid knip failure
// export function isAuditLogStreamingEnabled(currentProject?: CurrentProject): boolean {
//   return currentProject?.hasFeatureFlag(ProjectFeatureFlag.AuditLogStreaming) ?? false;
// }

export function isWarehouseMetricsEnabled(): boolean {
  return isFeatureFlagEnabledSync(FeatureFlag.WarehouseMetrics);
}

/**
 * Gates the logged-in user's identity (name/email/role/team) being sent to the
 * Mixpanel agent for personalized responses. Off means the chat request carries
 * no identity at all, and the agent renders no Current User prompt block.
 */
export function isAgentPersonalizationEnabled(): boolean {
  return isFeatureFlagEnabledSync(FeatureFlag.MixpanelAgentPersonalization);
}
